import React from "react";
import { createPortal } from "react-dom";
import { X, Check, Trash2, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize2, CircleSlash } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui";
import { Dialog } from "@/components";
import { Button, Textarea } from "@/components/ui";
import { usePhotoSlider, Photo } from "./usePhotoSlider";

interface PhotoSliderProps {
  isOpen: boolean;
  onClose: () => void;
  category: string | null;
  photos?: Photo[];
  onReview?: (photo: Photo, action: "APPROVED" | "REJECTED" | "DELETE", reason?: string) => void;
  showActions?: boolean;
}

const PhotoSlider: React.FC<PhotoSliderProps> = ({
  isOpen,
  onClose,
  category,
  photos = [],
  onReview,
  showActions = true,
}) => {
  const {
    activeIndex,
    showConfirmation,
    setShowConfirmation,
    actionType,
    reason,
    setReason,
    zoom,
    setZoom,
    position,
    setPosition,
    isDragging,
    handlePrev,
    handleNext,
    handleActionClick,
    handleConfirm,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    prevIndex,
    nextIndex,
    photoList,
    isActionDisabled,
    rejectionDetails,
    currentPhoto,
    approvalDetails
  } = usePhotoSlider({ isOpen, onClose, photos, onReview });

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[9999] bg-black/35 pointer-events-auto"
        aria-modal="true"
        role="dialog"
        aria-label={`Photo Slider ${category ? `- ${category}` : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* overlay click */}
        {/* <div className="absolute inset-0 z-[10] pointer-events-auto" onClick={(e) => e.stopPropagation()} /> */}

        <div className="relative flex min-h-screen items-center justify-center overflow-y-auto px-2 pt-10 pb-10 pointer-events-none">
          {/* Close */}
          

          <div className="relative w-full max-w-[800px] pointer-events-auto">
            <button
            onClick={onClose}
            className="absolute right-0 top-10 z-[100] text-white transition hover:text-gray-300 cursor-pointer pointer-events-auto"
          >
            <X className="h-8 w-8" />
          </button>
            <div className="relative mx-auto h-[540px] w-full">
              {/* Left back card */}
              {photoList.length > 1 && (
                <div className="absolute left-[-4%] top-1/2 z-10 w-[86%] -translate-y-1/2 overflow-hidden rounded-[22px] border-[6px] border-white bg-white opacity-95 shadow-xl">
                  <div className="h-[200px] w-full overflow-hidden">
                    <img
                      src={photoList[prevIndex]?.url}
                      alt="Previous"
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="h-[140px] bg-neutral-900/85" />
                </div>
              )}

              {/* Right back card */}
              {photoList.length > 1 && (
                <div className="absolute right-[-4%] top-1/2 z-10 w-[86%] -translate-y-1/2 overflow-hidden rounded-[22px] border-[6px] border-white bg-white opacity-95 shadow-xl">
                  <div className="h-[200px] w-full overflow-hidden">
                    <img
                      src={photoList[nextIndex]?.url}
                      alt="Next"
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="h-[140px] bg-neutral-900/85" />
                </div>
              )}

              {/* Active front card */}
              <div
                className="absolute left-1/2 top-1/2 z-30 w-[94%] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[22px] border-[8px] border-white bg-white shadow-[0_20px_60px_rgba(0,0,0,0.15)]"
                onClick={(e) => e.stopPropagation()}
              >
                <div 
                  className="relative h-[300px] w-full overflow-hidden bg-gray-900 rounded-t-[14px] flex items-center justify-center"
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  style={{ cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default" }}
                >
                  <img
                    src={currentPhoto?.url}
                    alt={`Photo ${activeIndex + 1}`}
                    className="h-full w-full object-contain pointer-events-none select-none"
                    style={{ 
                      transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
                      transition: isDragging ? "none" : "transform 0.3s ease-out"
                    }}
                  />
                  
                  {/* Zoom Controls Overlay */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center justify-center gap-4 bg-black/50 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20 z-40">
                    <button
                      onClick={() => {
                        setZoom((z: number) => Math.max(1, z - 0.5));
                        if (zoom <= 1.5) setPosition({ x: 0, y: 0 });
                      }}
                      className="text-white hover:text-yellow-400 transition cursor-pointer"
                      title="Zoom Out"
                    >
                      <ZoomOut className="h-5 w-5" />
                    </button>
                    <span className="text-white text-xs font-mono w-10 text-center select-none">
                      {Math.round(zoom * 100)}%
                    </span>
                    <button
                      onClick={() => setZoom((z: number) => Math.min(3, z + 0.5))}
                      className="text-white hover:text-yellow-400 transition cursor-pointer"
                      title="Zoom In"
                    >
                      <ZoomIn className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => {
                        setZoom(1);
                        setPosition({ x: 0, y: 0 });
                      }}
                      className="text-white hover:text-yellow-400 transition ml-2 cursor-pointer"
                      title="Reset Zoom"
                    >
                      <Maximize2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="bg-neutral-900/85 px-6 py-6 text-gray-900">
                  <div className="flex justify-between items-start gap-4">
                    <p className="max-w-[800px] text-[15px] leading-7 text-white">
                      {currentPhoto?.description || "No description provided"}
                    </p>
                    {rejectionDetails && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <CircleSlash className="h-6 w-6 text-red-400 cursor-pointer shrink-0" />
                        </PopoverTrigger>
                        <PopoverContent className="w-[350px] bg-red-200 border-none rounded-lg p-4 text-zinc-900 shadow-xl">
                          <div className="flex flex-col gap-2 text-[14px] font-medium leading-[1.4]">
                            <p>Rejected By : {rejectionDetails.reviewedBy} at {rejectionDetails.reviewedAt}</p>
                            <p>Reason : {rejectionDetails.reason}</p>
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}
                    {approvalDetails && (
                      <Check className="h-5 w-5 text-green-400" />
                    )}
                  </div>

                  {(showActions && onReview) && (
                    <div className="mt-6 flex items-center justify-center gap-3">
                      <Button
                        onClick={() => handleActionClick("rejected")}
                        disabled={isActionDisabled}
                        className="h-10 rounded-md bg-red-500 px-4 text-sm font-medium text-white hover:bg-red-600 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <CircleSlash className="h-4 w-4" />
                        Reject
                      </Button>

                      <Button
                        onClick={() => handleActionClick("approve")}
                        disabled={isActionDisabled}
                        className="h-10 rounded-md bg-green-500 px-4 text-sm font-medium text-white hover:bg-green-600 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Check className="h-4 w-4" />
                        Approve
                      </Button>

                      <Button
                        onClick={() => handleActionClick("delete")}
                        className="h-10 rounded-md bg-red-500 px-4 text-sm font-medium text-white hover:bg-red-600 cursor-pointer"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bottom arrows */}
            {photoList.length > 1 && (
              <div className="mt-3 flex items-center justify-center gap-4 z-[20]">
                <button
                  onClick={handlePrev}
                  className="flex h-14 w-14 items-center justify-center rounded-full border border-white bg-slate-200 text-slate-500 shadow cursor-pointer"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>

                <button
                  onClick={handleNext}
                  className="flex h-14 w-14 items-center justify-center rounded-full border border-white bg-slate-200 text-slate-500 shadow cursor-pointer"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {showConfirmation && (
        <Dialog
          open={showConfirmation}
          setOpen={setShowConfirmation}
          title={
            <span className="text-[20px] font-bold text-dialog-title">
              {actionType === "delete"
                ? "Delete Confirmation !"
                : actionType === "approve"
                  ? "Approval Confirmation !"
                  : "Reject Confirmation !"}
            </span>
          }
          onSave={handleConfirm}
          onCancel={() => setShowConfirmation(false)}
          saveText={
            actionType === "delete"
              ? "Yes, Delete"
              : actionType === "approve"
                ? "Yes, Approve"
                : "Yes, Reject"
          }
          cancelText="No"
          saveBtnClassName="h-11 rounded-xl bg-primary px-8 text-[15px] text-dialog-title hover:bg-primary-hover cursor-pointer"
          cancelBtnClassName="h-11 rounded-xl border-dialog-btn-no-border px-10 text-[15px] text-dialog-btn-no-text hover:bg-dialog-no-hover hover:text-dialog-btn-no-text cursor-pointer"
          contentClassName="sm:max-w-[550px]  !rounded-2xl border-none shadow-2xl overflow-hidden"
          childrenClassName="pt-4 pb-2"
        >
          <div className="pt-4 pb-2">
            <p className="text-[16px] text-dialog-message font-medium leading-[26px]">
              {actionType === "delete"
                ? "Are you sure you want to delete this image?"
                : actionType === "approve"
                  ? "Are you sure you want to approve this attachment?"
                  : "Are you sure you want to reject this image?"}
            </p>

            {(actionType === "delete" || actionType === "rejected") && (
              <div className="relative mt-10">
                <label className="absolute -top-[11px] left-4 z-10 bg-white px-2 text-[13px] font-medium text-dialog-label">
                  Reason
                </label>
                <Textarea
                  value={reason}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReason(e.target.value)}
                  placeholder="Enter reason for deletion"
                  className="min-h-[200px] w-full resize-none rounded-xl border border-dialog-border bg-white p-4 text-[15px] placeholder:text-dialog-label focus:border-primary focus:ring-0"
                />
              </div>
            )}
          </div>
        </Dialog>
      )}
    </>,
    document.body
  );
};

export default PhotoSlider;
